import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { getCcuaClient } from '~/apis/ccuaApi'
import { useMangoApiClient } from '~/atoms/MangoApi'
import { useUserState } from '~/atoms/UserState'
import {
  captureCcuaGatewayQueryFailure,
  retryTransientGatewayFailure,
  TRANSIENT_FAILURES_BEFORE_REPORT,
  useReportCcuaGatewayQueryError,
} from '~/queries/ccuaGatewaySentry'
import { AgentCustomStatusResponse } from '~/types/AgentCustomStatus'
import {
  AsyncQueryCancellation,
  UseQueryParameters,
} from '~/types/ApiRequestTypes'
import { UserAgentEventLog } from '~/types/UserAgentEventLog'
import { getPaginatedResultParts } from '~/utils/apiQueryUtils'

import { queryRetryDelay } from '@mangovoice/api-client'
import {
  collectAllGatewayItems,
  GATEWAY_MAX_PAGE_SIZE,
  mapGatewayEventLog,
  mapGatewayUserAgent,
  toDjangoPage,
  toRfc3339DayEnd,
  toRfc3339DayStart,
  type UserAgent,
} from '@mangovoice/cx-call-center-user-agents-client'
import { AgentStatus, ApiError, PaginatedApiResult } from '@mangovoice/types'

type UseGetAgentStatusParams = {
  extensionId?: number
} & UseQueryParameters<AgentStatus>
export const useGetAgentStatus = ({
  extensionId,
  onQueryStarted,
  onQuerySuccess,
  onQueryError,
  onQueryCancelled,
  options,
}: UseGetAgentStatusParams) => {
  const api = useMangoApiClient()
  const queryClient = useQueryClient()

  const getQueryKey = () => {
    const key = ['agentStatus', extensionId]
    return key
  }

  const tryCancelQueries: AsyncQueryCancellation = async () => {
    await queryClient.cancelQueries({
      queryKey: getQueryKey(),
    })
  }

  const { data, refetch: _refetch } = useQuery<AgentStatus, ApiError>({
    queryKey: getQueryKey(),
    queryFn: async ({ signal }) => {
      onQueryStarted?.()
      if (extensionId === undefined || extensionId === 0) {
        return Promise.reject({
          status: 400,
          message: 'Invalid extension ID',
        })
      }
      const { ok, data, status } =
        await api.queues.getQueueAgentStatus(extensionId)
      if (!ok) {
        const errorData = {
          status,
          ...(data as ApiError),
        }

        if (signal.aborted) {
          onQueryCancelled?.()
          return Promise.reject(errorData)
        }

        onQueryError?.(errorData)
        return Promise.reject(errorData)
      }

      const agentStatus = data as AgentStatus
      onQuerySuccess?.(agentStatus)
      return agentStatus
    },
    ...options,
    enabled: options?.enabled ?? false,
    retry: options?.retry ?? false,
  })

  const refetch = async () => {
    tryCancelQueries()
    return (await _refetch()).data
  }

  return { agentStatus: data?.status, refetch }
}

type UseQueueStatusChangeParams = {
  extensionId?: number
} & UseQueryParameters<undefined>

export const useQueueLogin = ({
  extensionId,
  onQueryStarted,
  onQuerySuccess,
  onQueryError,
  options,
}: UseQueueStatusChangeParams) => {
  const api = useMangoApiClient()

  const queueLoginMutation = useMutation({
    mutationFn: async () => {
      onQueryStarted?.()
      if (extensionId === undefined || extensionId === 0) {
        return Promise.reject({
          status: 400,
          message: 'Invalid extension ID',
        })
      }

      const { ok, status, data } = await api.queues.putQueueLogin(extensionId)

      if (!ok) {
        return Promise.reject({
          status,
          message: 'Failed to login to queue',
          ...(data as ApiError),
        })
      }

      return
    },
    onSuccess: () => {
      onQuerySuccess?.(undefined)
    },
    onError: (error: ApiError) => {
      onQueryError?.(error)
    },
    retry: options?.retry ?? 3,
  })

  const queueLogin = async (): Promise<any> => {
    await queueLoginMutation.mutateAsync()
  }

  return queueLogin
}

export const useQueueLogout = ({
  extensionId,
  onQueryStarted,
  onQuerySuccess,
  onQueryError,
  options,
}: UseQueueStatusChangeParams) => {
  const api = useMangoApiClient()

  const queueLogoutMutation = useMutation({
    mutationFn: async () => {
      onQueryStarted?.()
      if (extensionId === undefined || extensionId === 0) {
        return Promise.reject({
          status: 400,
          message: 'Invalid extension ID',
        })
      }

      const { ok, status, data } = await api.queues.putQueueLogout(extensionId)

      if (!ok) {
        return Promise.reject({
          status,
          message: 'Failed to logout from queue',
          ...(data as ApiError),
        })
      }

      return
    },
    onSuccess: () => {
      onQuerySuccess?.(undefined)
    },
    onError: (error: ApiError) => {
      onQueryError?.(error)
    },
    retry: options?.retry ?? 3,
  })

  const queueLogout = async (): Promise<any> => {
    await queueLogoutMutation.mutateAsync()
  }

  return queueLogout
}

type UpdateUserAgentStatusParams = UseQueryParameters<undefined>

export const useUpdateUserAgentStatus = ({
  onQueryStarted,
  onQuerySuccess,
  onQueryError,
}: UpdateUserAgentStatusParams) => {
  const { pbx } = useUserState()

  const updateUserAgentStatusMutation = useMutation({
    mutationFn: async ({
      customStatusId,
      ext_uuid,
      inQueue,
    }: {
      customStatusId: string | null
      ext_uuid?: string
      inQueue?: boolean
    }) => {
      onQueryStarted?.()
      if (ext_uuid === undefined || ext_uuid === '') {
        return Promise.reject({
          status: 400,
          message: 'Invalid extension ID',
        })
      }

      if (!pbx?.uuid) {
        return Promise.reject({
          status: 400,
          message: 'Missing PBX UUID for CCUA gateway',
        })
      }
      const { ok, status, data } = await getCcuaClient().patchUserAgent(
        pbx.uuid,
        ext_uuid,
        {
          agent_status_id: customStatusId,
          logged_in: inQueue,
        },
      )
      if (!ok) {
        return Promise.reject({
          status,
          message: 'Failed to update user agent status',
          ...(data as ApiError),
        })
      }

      return
    },
    onSuccess: () => {
      onQuerySuccess?.(undefined)
    },
    onError: (error: ApiError) => {
      captureCcuaGatewayQueryFailure(error, {
        enabled: true,
        pbxId: pbx?.id,
        source: 'update-user-agent-status',
        ignoreQueueMembershipNotFound: true,
      })
      onQueryError?.(error)
    },
  })

  const updateUserAgentStatus = async (
    customStatusId: string | null,
    ext_uuid?: string,
    inQueue?: boolean,
  ) => {
    await updateUserAgentStatusMutation.mutateAsync({
      customStatusId,
      ext_uuid,
      inQueue,
    })
  }

  return updateUserAgentStatus
}

/**
 * `onQueryStarted` and `onQueryError` run once per attempt, so a retried
 * no-response failure calls each of them twice per fetch.
 */
type UseGetUserAgentStatusParams = {
  ext_uuid?: string
} & UseQueryParameters<AgentCustomStatusResponse>
export const useGetUserAgentStatus = ({
  ext_uuid,
  onQueryStarted,
  onQuerySuccess,
  onQueryError,
  onQueryCancelled,
  options,
}: UseGetUserAgentStatusParams) => {
  const queryClient = useQueryClient()
  const { pbx } = useUserState()

  const getQueryKey = () => {
    const key = ['userAgentStatus', ext_uuid]
    return key
  }

  const tryCancelQueries: AsyncQueryCancellation = async () => {
    await queryClient.cancelQueries({
      queryKey: getQueryKey(),
    })
  }

  const query = useQuery<AgentCustomStatusResponse, ApiError>({
    queryKey: getQueryKey(),
    queryFn: async ({ signal }) => {
      onQueryStarted?.()
      if (ext_uuid === undefined || ext_uuid === '') {
        return Promise.reject({
          status: 400,
          message: 'Invalid extension ID',
        })
      }
      const response = await (async () => {
        if (!pbx?.uuid) {
          return {
            ok: false as const,
            data: { message: 'Missing PBX UUID for CCUA gateway' },
            status: 400,
          }
        }
        return getCcuaClient().getUserAgent(pbx.uuid, ext_uuid)
      })()
      const { ok, data, status } = response
      if (!ok) {
        const problem = 'problem' in response ? response.problem : undefined
        const errorData = {
          status,
          ...(problem ? { problem } : {}),
          ...(data as ApiError),
        }

        if (signal.aborted) {
          onQueryCancelled?.()
          return Promise.reject(errorData)
        }

        onQueryError?.(errorData)
        return Promise.reject(errorData)
      }

      const mapped = mapGatewayUserAgent(
        data as Parameters<typeof mapGatewayUserAgent>[0],
      )
      onQuerySuccess?.(mapped)
      return mapped
    },
    ...options,
    enabled: options?.enabled ?? false,
    retry: options?.retry ?? retryTransientGatewayFailure,
    retryDelay: options?.retryDelay ?? queryRetryDelay,
  })

  useReportCcuaGatewayQueryError({
    enabled: true,
    pbxId: pbx?.id,
    source: 'user-agent-status',
    queryKey: getQueryKey(),
    transientFailuresBeforeReport: TRANSIENT_FAILURES_BEFORE_REPORT,
    error: query.error,
    isError: query.isError,
    isSuccess: query.isSuccess,
  })

  const refetch = async () => {
    await tryCancelQueries()
    return (await query.refetch()).data
  }

  return { status: query.data, refetch }
}

export const useGetAgentStatuses = (pbxId?: number) => {
  const { pbx } = useUserState()
  const query = useQuery<
    PaginatedApiResult<AgentCustomStatusResponse>,
    ApiError
  >({
    queryKey: ['agentStatuses', pbxId],
    queryFn: async () => {
      if (!pbx?.uuid) {
        return Promise.reject('Missing PBX UUID for CCUA gateway')
      }
      const collected = await collectAllGatewayItems<UserAgent>((query) =>
        getCcuaClient().listUserAgents(pbx.uuid, query),
      )
      if (!collected.ok) {
        return Promise.reject('Failed to fetch agent statuses')
      }
      return {
        results: collected.items.map(mapGatewayUserAgent),
        count: collected.items.length,
        next: null,
        previous: null,
      }
    },
    enabled: !!pbxId,
  })

  useReportCcuaGatewayQueryError({
    enabled: true,
    pbxId: pbx?.id ?? pbxId,
    source: 'agent-statuses',
    queryKey: ['agentStatuses', pbxId],
    error: query.error,
    isError: query.isError,
    isSuccess: query.isSuccess,
  })

  return query
}

interface UserAgentEventLogQueryParams {
  pbx_id?: number
  user_uuid?: string
  start_date?: string
  end_date?: string
  limit?: number
}

type UseGetUserAgentEventLogsParams = {
  params?: UserAgentEventLogQueryParams
} & UseQueryParameters<PaginatedApiResult<UserAgentEventLog>>

const USER_AGENT_EVENT_LOGS_PAGE_SIZE = 50

export const useGetUserAgentEventLogs = ({
  params,
}: UseGetUserAgentEventLogsParams) => {
  const { pbx } = useUserState()
  const query = useInfiniteQuery<
    PaginatedApiResult<UserAgentEventLog>,
    ApiError
  >({
    // params must be in the key so a filter/date change starts a fresh
    // query rather than replaying stale page offsets.
    queryKey: ['userAgentEventLogs', params],
    queryFn: async ({ pageParam }) => {
      const offset = pageParam as number
      const limit = Math.min(
        params?.limit ?? USER_AGENT_EVENT_LOGS_PAGE_SIZE,
        GATEWAY_MAX_PAGE_SIZE,
      )
      if (!pbx?.uuid) {
        return Promise.reject('Missing PBX UUID for CCUA gateway')
      }
      const { ok, data } = await getCcuaClient().listEventLogs(pbx.uuid, {
        limit,
        offset,
        user_uuid: params?.user_uuid,
        since: params?.start_date
          ? toRfc3339DayStart(params.start_date)
          : undefined,
        until: params?.end_date ? toRfc3339DayEnd(params.end_date) : undefined,
      })
      if (!ok || !data) {
        return Promise.reject('Failed to fetch user agent event logs')
      }
      const page = toDjangoPage(data, { limit, offset })
      return {
        ...page,
        results: page.results.map(mapGatewayEventLog),
      }
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const parts = getPaginatedResultParts(lastPage)
      if (!parts?.hasNextPage || Number.isNaN(parts.nextPageOffset)) {
        return undefined
      }
      return parts.nextPageOffset
    },
    // A historical log doesn't need second-by-second freshness; keep pages
    // fresh for a minute so incidental re-renders / window refocus don't refetch
    // (and re-collapse the loaded pages) on their own.
    staleTime: 60_000,
  })

  useReportCcuaGatewayQueryError({
    enabled: true,
    pbxId: pbx?.id ?? params?.pbx_id,
    source: 'user-agent-event-logs',
    queryKey: ['userAgentEventLogs', params],
    error: query.error,
    isError: query.isError || query.isFetchNextPageError,
    isSuccess: query.isSuccess,
  })

  return query
}
